import { useCallback, useMemo } from 'react'

import { useVersionedBrandsQuery } from 'modules/brands/common/hooks/use-versioned-brands-query'
import { FeatureFlagsStore } from 'stores'

import { GET_BRAND_TERRITORIES_LOG, TOTAL_CONTEXT_TO_BRAND_TYPE } from './use-brand-territory-resolver.consts'
import type {
  IUseBrandTerritoryResolverProps,
  IUseBrandTerritoryResolverReturn,
} from './use-brand-territory-resolver.types'

export function useBrandTerritoryResolver({
  territoryId,
  brandContentType,
}: IUseBrandTerritoryResolverProps): IUseBrandTerritoryResolverReturn {
  const isTotal = brandContentType === 'total'
  const isNewPageBrandOverviewEnabled = FeatureFlagsStore.isFeatureFlagEnabled('isNewPageBrandOverviewEnabled')
  const shouldSkip = isNewPageBrandOverviewEnabled || !isTotal

  const {
    data: brandData,
    loading,
    called,
  } = useVersionedBrandsQuery({
    v1: 'getBrandIdList',
    v2: 'getBrandIdListV2',
    errorLog: GET_BRAND_TERRITORIES_LOG,
    options: {
      skip: shouldSkip,
    },
  })

  const brandTerritories = useMemo(() => {
    if (shouldSkip) return null

    const workspace = brandData?.getWorkspace
    if (!workspace) return null

    const workspaceBrand = workspace.workspaceBrand
    const competitors = workspace.workspaceBrandCompetitorsPaginated?.data ?? []
    const allBrands = [workspaceBrand, ...competitors]

    const currentBrand = allBrands.find(brand => {
      const territories = brand?.workspaceBrandTerritories
      if (!territories) return false
      return [
        territories.groupedTerritory?.territoryId,
        territories.selfContentTerritory?.territoryId,
        territories.earnedTerritory?.territoryId,
        territories.paidTerritory?.territoryId,
      ].includes(territoryId)
    })

    if (!currentBrand?.workspaceBrandTerritories) return null
    const territories = currentBrand.workspaceBrandTerritories
    return {
      owned: territories.selfContentTerritory?.territoryId ?? null,
      earned: territories.earnedTerritory?.territoryId ?? null,
      paid: territories.paidTerritory?.territoryId ?? null,
    }
  }, [brandData, shouldSkip, territoryId])

  const isReady = shouldSkip || (called && !loading && !!brandTerritories)

  const getTerritoryIdForContext = useCallback(
    (contextId: string): string => {
      if (!isTotal || isNewPageBrandOverviewEnabled || !brandTerritories) return territoryId

      const brandType = TOTAL_CONTEXT_TO_BRAND_TYPE[contextId]
      const resolved = brandType ? brandTerritories[brandType] : brandTerritories.owned

      return resolved ?? territoryId
    },
    [isTotal, isNewPageBrandOverviewEnabled, brandTerritories, territoryId]
  )

  return { getTerritoryIdForContext, isReady }
}
